import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { X, CheckCircle2, Circle, AlertTriangle, RotateCcw, ArrowRight, Award } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, formatIDR } from "@/lib/constants";
import { todayJKT } from "@/lib/tz";

const LIFECYCLE_COLOR = {
  active: "bg-sky-50 text-sky-700 border-sky-300",
  closing: "bg-amber-50 text-amber-700 border-amber-300",
  closed: "bg-emerald-50 text-emerald-700 border-emerald-300",
  cancelled: "bg-slate-100 text-slate-600 border-slate-300",
};

/**
 * Project Lifecycle Dialog — kelola transisi Aktif → Closing → Closed.
 * Read-only view untuk metrics dari Accurate Update Center.
 * Checklist dengan auto-suggest berdasarkan data finansial.
 */
export default function ProjectLifecycleDialog({ open, project, onClose, onChanged }) {
  const { user } = useAuth();
  const isBODSuper = user?.division === "BOD" && user?.level === 1;
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [moveClosingForm, setMoveClosingForm] = useState({
    physical_completion_date: todayJKT(),
    note: "",
  });
  const [reopenForm, setReopenForm] = useState({ reason: "", target_lifecycle: "active" });
  const [showReopen, setShowReopen] = useState(false);

  const load = async () => {
    if (!project?.id) return;
    try {
      const { data } = await api.get(`/project-lifecycle/${project.id}`);
      setData(data);
    } catch (e) { toast.error("Gagal memuat lifecycle"); }
  };

  useEffect(() => { if (open && project?.id) load(); }, [open, project?.id]); // eslint-disable-line

  if (!open || !project) return null;

  const lifecycle = data?.lifecycle_status || "active";
  const canMoveToClosing = lifecycle === "active";
  const canCloseNow = lifecycle === "closing" && data?.checklist_all_required_checked;
  const canReopen = ["closed", "closing", "cancelled"].includes(lifecycle) && isBODSuper;
  const isClosed = lifecycle === "closed";

  const doMoveToClosing = async () => {
    if (!moveClosingForm.physical_completion_date) { toast.error("Tanggal Selesai Fisik wajib"); return; }
    setSaving(true);
    try {
      await api.post(`/project-lifecycle/${project.id}/move-to-closing`, moveClosingForm);
      toast.success("Project dipindah ke status Closing");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  const doToggleChecklist = async (key, checked) => {
    if (isClosed) return;
    const items = (data?.checklist || []).map((it) => ({
      key: it.key,
      checked: it.key === key ? checked : !!it.checked,
      note: it.note || null,
    }));
    try {
      const { data: newData } = await api.patch(`/project-lifecycle/${project.id}/closing-checklist`, { items });
      setData(newData);
      onChanged?.();
    } catch (e) { toast.error("Gagal update checklist"); }
  };

  const doClose = async (override = false) => {
    if (!window.confirm("Yakin menutup project ini secara permanen?")) return;
    setSaving(true);
    try {
      await api.post(`/project-lifecycle/${project.id}/close`, { override_incomplete: override });
      toast.success("Project berhasil di-Close");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal close"); }
    finally { setSaving(false); }
  };

  const doReopen = async () => {
    if (!reopenForm.reason.trim()) { toast.error("Alasan wajib"); return; }
    setSaving(true);
    try {
      await api.post(`/project-lifecycle/${project.id}/reopen`, reopenForm);
      toast.success("Project di-Reopen");
      setShowReopen(false);
      setReopenForm({ reason: "", target_lifecycle: "active" });
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal reopen"); }
    finally { setSaving(false); }
  };

  const m = data?.metrics;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="lifecycle-dialog">
      <div className="bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#0B1D3A] text-white px-5 py-3 flex items-center justify-between z-10">
          <div>
            <h2 className="font-display font-bold text-lg">Lifecycle Project</h2>
            <div className="text-xs text-white/70">{project?.project_name}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 border text-xs uppercase tracking-wider font-semibold ${LIFECYCLE_COLOR[lifecycle]}`} data-testid="lifecycle-badge">
              {data?.lifecycle_labels?.[lifecycle] || lifecycle}
            </span>
            <button data-testid="btn-close-lifecycle" onClick={onClose} className="text-white/70 hover:text-white p-1"><X size={20} /></button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {!data ? (
            <div className="py-8 text-center text-slate-500 text-sm">Memuat data…</div>
          ) : (
            <>
              {/* Timeline */}
              <div className="grid grid-cols-4 gap-1" data-testid="lifecycle-timeline">
                {[["active", "Aktif"], ["closing", "Penutupan"], ["closed", "Ditutup"], ["cancelled", "Dibatalkan"]].map(([k, l]) => (
                  <div key={k} className={`px-3 py-2 border-2 text-center ${lifecycle === k ? LIFECYCLE_COLOR[k] + " font-bold" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                    <div className="text-[10px] uppercase tracking-wider">{l}</div>
                  </div>
                ))}
              </div>

              {/* Physical completion + close info */}
              {data.physical_completion_date && (
                <div className="bg-amber-50 border border-amber-300 p-3 text-sm">
                  <span className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Selesai Fisik: </span>
                  <span className="tabular-nums">{fmtDate(data.physical_completion_date)}</span>
                </div>
              )}
              {data.closed_at && (
                <div className="bg-emerald-50 border border-emerald-300 p-3 text-sm">
                  <span className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Closed pada: </span>
                  <span className="tabular-nums">{fmtDate(data.closed_at)}</span>
                </div>
              )}

              {/* Metrics — Accurate driven (read-only) */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-semibold flex items-center gap-2">
                  Metrik Finansial (Read-Only — Pembukuan resmi dilakukan di software akuntansi terpisah)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Metric label="Kontrak" value={formatIDR(m?.contract_value)} />
                  <Metric label="Invoice Tercatat (DPP)" value={formatIDR(m?.total_invoice_dpp)} status={m?.invoice_gap <= 100 ? "ok" : "warn"} statusLabel={m?.invoice_gap <= 100 ? "Lengkap" : `Sisa ${formatIDR(m?.invoice_gap)}`} />
                  <Metric label="Pembayaran Diterima" value={formatIDR(m?.total_paid)} status={m?.outstanding <= 100 ? "ok" : "warn"} statusLabel={m?.outstanding <= 100 ? "Lunas" : `Outstanding ${formatIDR(m?.outstanding)}`} />
                  <Metric label="Retensi Ditahan / Diklaim" value={`${formatIDR(m?.total_retention_held)} / ${formatIDR(m?.total_retention_claimed)}`} status={m?.retention_outstanding <= 100 ? "ok" : "warn"} statusLabel={m?.retention_outstanding <= 100 ? "Selesai" : `Sisa ${formatIDR(m?.retention_outstanding)}`} />
                  <Metric label="Total Expense (Final Cost)" value={formatIDR(m?.total_expense)} />
                  {m?.estimated_final_margin !== null && m?.estimated_final_margin !== undefined && (
                    <Metric label="Est. Final Margin" value={formatIDR(m?.estimated_final_margin)} />
                  )}
                  {(m?.children || []).length > 0 && (
                    <Metric label={`VO/WO Anak (${m.children.length})`} value={`${m.children.filter((c) => c.lifecycle_status === "closed").length} closed`} />
                  )}
                </div>
              </div>

              {/* Move to Closing action */}
              {canMoveToClosing && (
                <div className="bg-white border-2 border-amber-300 p-4">
                  <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold mb-2">Pindahkan ke Status Closing</div>
                  <div className="text-xs text-slate-600 mb-3">Pekerjaan fisik selesai tapi administrasi (invoice, pembayaran, retensi, asset) belum tuntas.</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Tanggal Selesai Fisik *</label>
                      <input type="date" data-testid="input-physical-completion" value={moveClosingForm.physical_completion_date}
                        onChange={(e) => setMoveClosingForm({ ...moveClosingForm, physical_completion_date: e.target.value })}
                        className="w-full border border-slate-300 px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Catatan</label>
                      <input type="text" data-testid="input-closing-note" value={moveClosingForm.note}
                        onChange={(e) => setMoveClosingForm({ ...moveClosingForm, note: e.target.value })}
                        placeholder="mis: menunggu BAST + pembayaran termin akhir" className="w-full border border-slate-300 px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <button data-testid="btn-move-to-closing" disabled={saving} onClick={doMoveToClosing}
                    className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 flex items-center gap-1">
                    <ArrowRight size={12} /> Pindah ke Closing
                  </button>
                </div>
              )}

              {/* Checklist */}
              {(lifecycle === "closing" || lifecycle === "closed") && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      Checklist Closing ({(data.checklist || []).filter((c) => c.checked).length}/{(data.checklist || []).length})
                    </div>
                    {data.ready_to_close && !isClosed && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-bold uppercase tracking-wider" data-testid="lifecycle-ready-to-close">
                        ✓ SIAP DI-CLOSED
                      </span>
                    )}
                  </div>
                  <div className="space-y-1" data-testid="lifecycle-checklist">
                    {(data.checklist || []).map((it) => (
                      <label key={it.key} className={`flex items-start gap-2 p-2 border cursor-pointer ${it.checked ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                        <input type="checkbox" checked={!!it.checked} disabled={isClosed}
                          data-testid={`closing-check-${it.key}`}
                          onChange={(e) => doToggleChecklist(it.key, e.target.checked)}
                          className="mt-0.5" />
                        <div className="flex-1">
                          <div className={`text-sm ${it.checked ? "line-through text-slate-500" : "text-slate-800"}`}>
                            {it.label}
                            {it.required && <span className="ml-1 text-rose-500 text-[10px]">*wajib</span>}
                            {it.auto_suggest && !it.checked && (
                              <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-sky-100 text-sky-700 border border-sky-300 uppercase tracking-wider">
                                ✓ Terpenuhi otomatis
                              </span>
                            )}
                          </div>
                          {it.checked && it.checked_by_name && (
                            <div className="text-[10px] text-slate-400">✓ oleh {it.checked_by_name} · {fmtDate(it.checked_at)}</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Close button */}
                  {canCloseNow && (
                    <button data-testid="btn-lifecycle-close" onClick={() => doClose(false)} disabled={saving}
                      className="mt-3 w-full px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-1 disabled:opacity-50">
                      <CheckCircle2 size={14} /> Close Project (Semua checklist ✓)
                    </button>
                  )}
                  {lifecycle === "closing" && !canCloseNow && isBODSuper && (
                    <button data-testid="btn-lifecycle-close-override" onClick={() => doClose(true)} disabled={saving}
                      className="mt-3 w-full px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center gap-1 disabled:opacity-50">
                      <AlertTriangle size={14} /> Force Close (Override - BOD Lv1)
                    </button>
                  )}
                </div>
              )}

              {/* Reopen */}
              {canReopen && (
                <div className="border-t border-slate-200 pt-3">
                  {!showReopen ? (
                    <button data-testid="btn-lifecycle-reopen-open" onClick={() => setShowReopen(true)}
                      className="px-4 py-2 text-xs uppercase tracking-wider font-semibold border border-slate-300 hover:bg-slate-50 flex items-center gap-1 text-slate-700">
                      <RotateCcw size={12} /> Reopen Project (BOD Lv1)
                    </button>
                  ) : (
                    <div className="bg-slate-50 border border-slate-300 p-3 space-y-2">
                      <div className="text-[10px] uppercase tracking-wider text-slate-700 font-semibold">Reopen Project</div>
                      <textarea rows={2} data-testid="input-reopen-reason" value={reopenForm.reason}
                        onChange={(e) => setReopenForm({ ...reopenForm, reason: e.target.value })}
                        placeholder="Alasan reopen (wajib untuk audit log)"
                        className="w-full border border-slate-300 px-3 py-2 text-sm" />
                      <select data-testid="select-reopen-target" value={reopenForm.target_lifecycle}
                        onChange={(e) => setReopenForm({ ...reopenForm, target_lifecycle: e.target.value })}
                        className="w-full border border-slate-300 px-3 py-2 text-sm">
                        <option value="active">Kembali ke Aktif</option>
                        <option value="closing">Kembali ke Closing</option>
                      </select>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setShowReopen(false)} className="px-3 py-1 text-xs uppercase tracking-wider border border-slate-300">Batal</button>
                        <button data-testid="btn-lifecycle-reopen-submit" disabled={saving} onClick={doReopen}
                          className="px-3 py-1 text-xs uppercase tracking-wider bg-[#FF5A1F] hover:bg-[#e04d15] text-white disabled:opacity-50">Reopen</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* History */}
              {(data.history || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">Histori Transisi</div>
                  <div className="space-y-1 text-xs" data-testid="lifecycle-history">
                    {data.history.map((h) => (
                      <div key={h.id} className="border-l-2 border-slate-200 pl-3 py-1">
                        <div className="text-[10px] text-slate-500">{fmtDate(h.created_at)} · {h.actor_name} ({h.actor_division} Lv{h.actor_level})</div>
                        <div className="text-slate-700">
                          <span className="font-mono">{h.from_state}</span> → <span className="font-mono font-bold">{h.to_state}</span>
                          {h.reason && <span className="ml-2 text-slate-500">— {h.reason}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, status, statusLabel }) {
  const tone = status === "ok" ? "border-emerald-300 bg-emerald-50" : status === "warn" ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white";
  return (
    <div className={`border p-2 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-semibold tabular-nums text-slate-800 leading-tight">{value}</div>
      {statusLabel && <div className={`text-[10px] mt-0.5 ${status === "ok" ? "text-emerald-700" : "text-amber-700"} font-semibold`}>{statusLabel}</div>}
    </div>
  );
}
